import { InfiniteData } from "@tanstack/react-query";
import { UseTellescopeInfiniteQueryOptions, UseTellescopeInfiniteQueryResult } from "../../types";
import { useTellescopeInfiniteQuery } from "../../use-tellescope-infinite-query";

const isValidQuery = (parsedQuery: unknown): parsedQuery is HealthiePatientQueryRequest => {
  try {
    if (typeof parsedQuery !== "object") return false;
    if (parsedQuery === null) return false;
    if (
      !Object.entries(parsedQuery).some(([key, value]) => {
        switch (key) {
          case "pageSize":
            if (typeof value === "undefined" || value === null || typeof value === "number") return true;
            return false;
          case "keywords":
          case "cursor":
            if (typeof value === "undefined" || value === null || typeof value === "string") return true;
            return false;
          default:
            return false;
        }
      })
    ) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const parseQuery = (query: string | null | undefined): HealthiePatientQueryRequest | undefined => {
  if (!query) return undefined;
  try {
    const parsedQuery = JSON.parse(query);
    if (isValidQuery(parsedQuery)) {
      return parsedQuery;
    }
    return undefined;
  } catch {
    return undefined;
  }
};

export interface HealthiePatient {
  id: string | null;
  first_name: string | null;
  last_name: string | null;
  legal_name: string | null;
  email: string | null;
  phone_number: string | null;
  additional_phone_number: string | null;
  /** YYYY-MM-DD */
  dob: string | null;
  gender: string | null;
  sex: string | null;
  height: unknown | null;
  timezone: unknown | null;
  active: unknown | null;
  created_at: string | null;
  group_name: string | null;
  record_identifier: string | null;
  additional_record_identifier: string | null;
  preffered_language_code: unknown | null;
  dietitian_id: string | null;
  active_tags: { name: string }[] | null;
  location: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    zip: unknown | null;
    country: string | null;
  } | null;
  cursor: string | null;
}

export interface HealthiePatientQueryRequest {
  /** Default: 25, max: 100 */
  pageSize?: number;
  cursor?: string | null;
  keywords?: string;
}

export interface HealthiePatientQueryResponse {
  data: {
    users: HealthiePatient[];
    /** Total matching patients */
    count: number;
    /** null === last page */
    nextCursor: string | null;
  };
}

export interface UseHealthiePatientsQueryOptions<TError = Error>
  extends Omit<
    UseTellescopeInfiniteQueryOptions<
      "integrations",
      "proxy_read",
      HealthiePatientQueryResponse["data"]["nextCursor"],
      TError,
      HealthiePatient[]
    >,
    "model" | "method" | "request" | "getNextPageParam" | "initialPageParam" | "paginateRequest" | "select"
  > {
  request?: HealthiePatientQueryRequest;
}

const selectAllPatients = (
  data: InfiniteData<HealthiePatientQueryResponse, string | null>
): { patients: HealthiePatient[]; count: number } => ({
  patients: data.pages.flatMap((page) => page.data.users),
  count: data.pages.at(-1)?.data.count ?? Infinity,
});

export const useHealthiePatientsQuery = <TError = Error>({
  request,
  ...options
}: UseHealthiePatientsQueryOptions<TError> = {}): UseTellescopeInfiniteQueryResult<
  "integrations",
  "proxy_read",
  TError,
  { patients: HealthiePatient[]; count: number }
> =>
  useTellescopeInfiniteQuery<
    "integrations",
    "proxy_read",
    string | null,
    TError,
    { patients: HealthiePatient[]; count: number }
  >({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Healthie",
      type: "patients",
      query: Object.values(request ?? {}).some(Boolean) ? JSON.stringify(request) : undefined,
    },
    initialPageParam: "",
    getNextPageParam: (lastPage: HealthiePatientQueryResponse) => {
      return lastPage.data.nextCursor ?? undefined;
    },
    paginateRequest: (cursor, { query, ...request }) => {
      if (!cursor)
        return {
          ...request,
          query,
        };
      const parsedQuery = parseQuery(query);

      return {
        ...request,
        query: JSON.stringify({
          ...parsedQuery,
          cursor,
        }),
      };
    },
    select: selectAllPatients,
    ...options,
  });
